import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import Editable from "@/components/Editable";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, CalendarCheck, MapPin, Users, PlayCircle, X, CheckCircle, MagnifyingGlass, Funnel, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import { SERVICES } from "@/data/services";

// Heuristic mapping from a class series name → matching service slug
function pickServiceSlug(name) {
  const n = (name || "").toLowerCase();
  // Most common direct mappings
  const direct = [
    ["puppy", "puppy-school"],
    ["scent", "scent-detection"],
    ["activit", "activities-class"],
    ["walk", "walk-and-trains"],
    ["walk & train", "walk-and-trains"],
    ["home check", "home-check-ins"],
    ["check-in", "home-check-ins"],
    ["consult", "private-consults"],
    ["private", "private-consults"],
    ["recall", "private-consults"],
    ["board", "board-train"],
    ["foundation", "puppy-school"],
    ["beginner", "puppy-school"],
  ];
  for (const [kw, slug] of direct) {
    if (n.includes(kw)) return slug;
  }
  // Fall back to title containment match against SERVICES
  for (const s of SERVICES) {
    const t = (s.title || "").toLowerCase();
    if (t && n.includes(t)) return s.slug;
    if (s.slug && n.includes(s.slug.replace(/-/g, " "))) return s.slug;
  }
  return null;
}

const ALL = "all";
const EMPTY_FORM = { name: "", email: "", phone: "", suburb: "", dog_name: "", dog_age: "", dog_dob: "", notes: "" };

export default function CoursesInPerson() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [location, setLocation] = useState(ALL);
  const [classType, setClassType] = useState(ALL);
  const [weekday, setWeekday] = useState(ALL);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [q, setQ] = useState("");
  const [enrolFor, setEnrolFor] = useState(null);
  const [enrolSent, setEnrolSent] = useState(false);
  const [enrolBusy, setEnrolBusy] = useState(false);
  const [enrolForm, setEnrolForm] = useState(EMPTY_FORM);
  const [paying, setPaying] = useState(false);
  const [step, setStep] = useState(1); // 1=date/pet, 2=client, 3=account/pay
  const [prereqChecks, setPrereqChecks] = useState({});
  const [ageError, setAgeError] = useState("");
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  // Active enrolment price after any applied coupon discount
  const priceAud = Number(enrolFor?.price) || 0;
  const finalAud = appliedCoupon ? Number(appliedCoupon.final_aud) : priceAud;

  const applyCoupon = async () => {
    if (!coupon.trim() || !enrolFor) return;
    setCouponBusy(true);
    try {
      const r = await api.post("/coupons/validate", {
        code: coupon.trim(),
        amount_aud: priceAud,
        tx_type: "class_enrol",
      });
      setAppliedCoupon(r.data);
      toast.success(`Coupon applied — ${r.data.discount_type === "percent" ? `${r.data.amount}% off` : `$${r.data.amount} off`}`);
    } catch (e) {
      setAppliedCoupon(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally { setCouponBusy(false); }
  };

  const removeCoupon = () => { setAppliedCoupon(null); setCoupon(""); };

  useEffect(() => {
    api.get("/class-sessions?course_type=in_person&upcoming_only=true").then((r) => setSessions(r.data)).catch(() => {});
    // Track interest — logged-in clients viewing the in-person catalogue.
    api.post("/activity/track-interest", { kind: "class_series", ref_id: "in-person-catalogue", label: "In-person classes catalogue" }).catch(() => {});
  }, []);

  // Track interest when the enrol modal opens for a SPECIFIC class series —
  // a much higher-signal action than just browsing the list.
  useEffect(() => {
    if (!enrolFor) return;
    api.post("/activity/track-interest", {
      kind: "class_series",
      ref_id: enrolFor.series_id || enrolFor.id || "",
      label: enrolFor.series_name || enrolFor.title || "Class series",
    }).catch(() => {});
  }, [enrolFor]);

  // Pre-fill the enrolment form from the signed-in user when the modal
  // opens. Saves a logged-in client typing their own contact details again.
  useEffect(() => {
    if (enrolFor && user) {
      setEnrolForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
        suburb: f.suburb || user.suburb || "",
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolFor, user]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("canceled") === "1") {
      toast.info("Checkout cancelled — you weren't charged. Pick a class to try again whenever you're ready.");
      params.delete("canceled");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  // Calculate puppy age in weeks at first class & validate against series cap
  function validateAge() {
    if (!enrolFor || !enrolForm.dog_dob) return "";
    const max = enrolFor.max_age_weeks;
    const min = enrolFor.min_age_weeks;
    if (!max && !min) return "";
    try {
      const dob = new Date(enrolForm.dog_dob);
      const cls = new Date(enrolFor.starts_at);
      const weeks = Math.floor((cls - dob) / (1000 * 60 * 60 * 24 * 7));
      if (weeks < 0) return "Date of birth must be before the class starts.";
      if (max && weeks > max) return `Your dog will be ${weeks} weeks old at the first class — this class is for puppies up to ${max} weeks. Try a teenage/adult class instead.`;
      if (min && weeks < min) return `Your dog will be only ${weeks} weeks old at the first class — minimum age is ${min} weeks.`;
      return "";
    } catch {
      return "";
    }
  }

  function prereqsOk() {
    const list = (enrolFor && enrolFor.prerequisites) || [];
    return list.every((p) => prereqChecks[p]);
  }

  async function payAndEnrol(spotType = "working") {
    if (!enrolFor) return;
    // Guests are welcome — we now accept name/email/phone/dog details at
    // checkout, send them to Stripe, and offer Log in / Create account on
    // the post-payment landing page. No mid-flow auth detour.
    setPaying(true);
    try {
      const r = await api.post("/payments/checkout/class-session", {
        session_id: enrolFor.id,
        spot_type: spotType,
        origin_url: window.location.origin,
        dog_dob: enrolForm.dog_dob || "",
        prerequisites_confirmed: Object.keys(prereqChecks).filter((k) => prereqChecks[k]),
        coupon_code: appliedCoupon ? appliedCoupon.code : null,
        // Guest contact details — ignored by the backend when the visitor is
        // already authenticated (their session takes precedence).
        ...(user ? {} : {
          name: enrolForm.name || "",
          email: enrolForm.email || "",
          phone: enrolForm.phone || "",
          suburb: enrolForm.suburb || "",
          dog_name: enrolForm.dog_name || "",
        }),
      });
      window.location.href = r.data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = err?.response?.status;
      const msg = detail
        ? `${detail}${code ? ` (HTTP ${code})` : ""}`
        : `Couldn't start checkout — please try again.${code ? ` (HTTP ${code})` : ""}`;
      toast.error(msg);
      setPaying(false);
      return; // don't run the finally setPaying again
    }
    // We intentionally leave `paying = true` on success — the browser is
    // about to redirect to Stripe; resetting state causes a brief UI flicker.
  }

  function closeEnrol() {
    setEnrolFor(null);
    setEnrolSent(false);
    setEnrolForm(EMPTY_FORM);
    setStep(1);
    setPrereqChecks({});
    setAgeError("");
    setCoupon("");
    setAppliedCoupon(null);
  }

  async function submitEnrol(e) {
    e.preventDefault();
    if (!enrolFor) return;
    setEnrolBusy(true);
    try {
      await api.post("/enrol-requests", {
        series_id: enrolFor.series_id || null,
        session_id: enrolFor.id,
        ...enrolForm,
        prerequisites_confirmed: Object.keys(prereqChecks).filter((k) => prereqChecks[k]),
      });
      setEnrolSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sorry — couldn't send your request. Please try again.");
    } finally {
      setEnrolBusy(false);
    }
  }

  const locations = Array.from(new Set(sessions.map((s) => s.location))).filter(Boolean).sort();
  // Class types — derive from each session's title with the trailing
  // "— Session N" stripped, so "Puppy School April Intake — Session 2"
  // becomes "Puppy School April Intake" and groups sensibly.
  const classTypes = Array.from(new Set(sessions.map((s) => (s.title || "").replace(/ — Session \d+$/, "").trim()))).filter(Boolean).sort();

  const WEEKDAYS = [
    { v: "1", label: "Monday" }, { v: "2", label: "Tuesday" }, { v: "3", label: "Wednesday" },
    { v: "4", label: "Thursday" }, { v: "5", label: "Friday" }, { v: "6", label: "Saturday" },
    { v: "0", label: "Sunday" },
  ];

  const filtered = useMemo(() => sessions.filter((s) => {
    if (location !== ALL && s.location !== location) return false;
    if (classType !== ALL) {
      const baseTitle = (s.title || "").replace(/ — Session \d+$/, "").trim();
      if (baseTitle !== classType) return false;
    }
    if (weekday !== ALL) {
      // Use Brisbane day-of-week so the filter aligns with what the customer
      // sees on screen (avoids midnight-near-tz-boundary surprises).
      const bneDay = new Intl.DateTimeFormat("en-GB", { timeZone: "Australia/Brisbane", weekday: "short" }).format(new Date(s.starts_at));
      const map = { Sun: "0", Mon: "1", Tue: "2", Wed: "3", Thu: "4", Fri: "5", Sat: "6" };
      if (map[bneDay] !== weekday) return false;
    }
    if (fromDate && new Date(s.starts_at) < new Date(fromDate)) return false;
    if (toDate) {
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);
      if (new Date(s.starts_at) > end) return false;
    }
    if (q.trim()) {
      const t = q.trim().toLowerCase();
      const hay = `${s.title || ""} ${s.location || ""} ${s.description || ""}`.toLowerCase();
      if (!hay.includes(t)) return false;
    }
    return true;
  }), [sessions, location, classType, weekday, fromDate, toDate, q]);

  // Group by series, sort sessions within each group chronologically, and
  // sort the groups themselves so the NEAREST upcoming intake appears first.
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((s) => {
      const key = s.series_id || s.id;
      if (!map[key]) map[key] = { series: s, items: [] };
      map[key].items.push(s);
    });
    Object.values(map).forEach((g) => g.items.sort((a, b) => a.starts_at.localeCompare(b.starts_at)));
    // Replace `series` with the earliest session so the card always shows
    // the nearest upcoming date / time / capacity, not whatever order the
    // API returned in.
    Object.values(map).forEach((g) => { g.series = g.items[0]; });
    return Object.fromEntries(
      Object.entries(map).sort(([, a], [, b]) => a.series.starts_at.localeCompare(b.series.starts_at))
    );
  }, [filtered]);

  function resetFilters() {
    setLocation(ALL);
    setClassType(ALL);
    setWeekday(ALL);
    setFromDate("");
    setToDate("");
    setQ("");
  }

  // Deep-link support: if the URL has ?series=<id>, scroll to that card and prime the enrol modal.
  useEffect(() => {
    if (typeof window === "undefined" || !sessions.length) return;
    const params = new URLSearchParams(window.location.search);
    const seriesParam = params.get("series");
    if (!seriesParam) return;
    const targetSession = sessions.find((s) => (s.series_id || s.id) === seriesParam);
    if (!targetSession) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="series-${seriesParam}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#f38838]", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#f38838]", "ring-offset-2"), 4000);
      }
      setEnrolFor(targetSession);
    }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions.length]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo title="In-Person Puppy Classes | Puppl Sunshine Coast" description="Upcoming Puppy School, Teenage Puppy School and Scent Detection classes at our Sunshine Coast halls — Mudjimba, Kawana and Eudlo." canonical={typeof window !== "undefined" ? window.location.origin + "/courses/in-person" : ""} />
      <PublicHeader />

      <section className="bg-[#1b4a76] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <Editable id="courses_inperson.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-3" defaultHtml="In-person classes" />
          <Editable id="courses_inperson.heading" as="h1" className="font-heading text-4xl lg:text-6xl font-bold leading-tight mb-4 max-w-3xl" defaultHtml="Train with the team, on the Coast." />
          <Editable id="courses_inperson.subtitle" as="p" className="text-lg lg:text-xl opacity-90 max-w-2xl" defaultHtml="Small group puppy school, teenage classes and specialist scent work at three convenient Sunshine Coast halls. Browse upcoming intakes below." />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" className="btn-primary inline-flex items-center gap-2">Book a Spot <ArrowRight size={18} weight="bold" /></Link>
            <Link to="/courses/online" className="bg-white/15 backdrop-blur-sm hover:bg-white/25 text-white border border-white/30 rounded-xl px-6 py-3 font-semibold transition-colors inline-flex items-center gap-2">
              <PlayCircle size={18} weight="duotone" /> Prefer online?
            </Link>
          </div>
        </div>
      </section>

      <section className="py-10 px-6 lg:px-12 max-w-7xl mx-auto">
        {/* FILTER BAR */}
        <div className="card-pp p-4 sm:p-5 mb-6" data-testid="class-filters">
          <div className="flex items-center gap-2 mb-3 text-stone-700">
            <Funnel size={16} weight="duotone" className="text-[#f38838]" />
            <span className="text-sm font-semibold">Find a class</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="relative lg:col-span-2">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input data-testid="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search class name…" className="w-full rounded-lg border border-stone-200 pl-9 pr-3 py-2.5 text-sm" />
            </div>
            <select data-testid="filter-class-type" value={classType} onChange={(e) => setClassType(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm bg-white">
              <option value={ALL}>All class types</option>
              {classTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select data-testid="filter-location-select" value={location} onChange={(e) => setLocation(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm bg-white">
              <option value={ALL}>All locations</option>
              {locations.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
            </select>
            <select data-testid="filter-weekday" value={weekday} onChange={(e) => setWeekday(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm bg-white">
              <option value={ALL}>Any day of week</option>
              {WEEKDAYS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2 sm:col-span-2 lg:col-span-1">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold block mb-1">From</label>
                <input data-testid="filter-from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full rounded-lg border border-stone-200 px-2 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold block mb-1">To</label>
                <input data-testid="filter-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-full rounded-lg border border-stone-200 px-2 py-2 text-sm" />
              </div>
            </div>
          </div>
          {(location !== ALL || classType !== ALL || weekday !== ALL || fromDate || toDate || q) && (
            <div className="mt-3 flex items-center justify-between flex-wrap gap-2 text-xs text-stone-500">
              <span>{Object.keys(grouped).length} class{Object.keys(grouped).length !== 1 && "es"} match · sorted by nearest first</span>
              <button onClick={resetFilters} data-testid="filter-reset" className="text-[#f38838] font-semibold hover:underline">Clear filters</button>
            </div>
          )}
          {!(location !== ALL || classType !== ALL || weekday !== ALL || fromDate || toDate || q) && Object.keys(grouped).length > 0 && (
            <div className="mt-3 text-xs text-stone-500" data-testid="filter-sort-hint">Showing all upcoming intakes · nearest first</div>
          )}
        </div>

        <div className="space-y-6">
          {Object.values(grouped).length === 0 ? (
            <div className="text-center py-16 text-stone-500">
              <CalendarCheck size={48} weight="duotone" className="mx-auto mb-3 text-stone-300" />
              No matching classes. <Link to="/contact" className="text-[#f38838] font-semibold">Get notified</Link> when new dates open.
            </div>
          ) : (
            Object.values(grouped).map(({ series, items }) => {
              const first = items[0];
              const enrolled = first.enrolled_count || 0;
              const capacity = first.capacity || 6;
              const spotsLeft = Math.max(0, capacity - enrolled);
              const full = spotsLeft <= 0;
              return (
                <article key={series.series_id || series.id} className="card-pp p-6 lg:p-8 relative" data-testid={`series-${series.series_id || series.id}`}>
                  {full && <span className="absolute top-4 right-4 bg-red-600 text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md" data-testid={`full-badge-${series.series_id || series.id}`}>Full</span>}
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div className="flex-1 min-w-[260px]">
                      <h3 className="font-heading text-2xl font-bold text-stone-900 mb-1">{series.title?.replace(/ — Session \d+$/, "")}</h3>
                      <StartsInBadge startsAt={first.starts_at} seriesId={series.series_id || series.id} />
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-600 mt-2">
                        <div className="flex items-center gap-1.5"><MapPin size={14} weight="duotone" className="text-[#f38838]" /> {series.location}</div>
                        <div className="flex items-center gap-1.5">
                          <Users size={14} weight="duotone" className={full ? "text-red-500" : spotsLeft <= 2 ? "text-amber-500" : "text-emerald-600"} />
                          {full ? <span className="font-bold text-red-600">FULL</span> : <span className={spotsLeft <= 2 ? "font-bold text-amber-700" : ""}>{spotsLeft} spot{spotsLeft !== 1 && "s"} left</span>}
                        </div>
                        <div className="flex items-center gap-1.5"><CalendarCheck size={14} weight="duotone" className="text-[#1b4a76]" /> {items.length} session{items.length !== 1 && "s"}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-3xl font-bold text-[#f38838]">${first.price}</div>
                      <div className="text-xs text-stone-500">AUD total</div>
                    </div>
                  </div>

                  <div className="bg-[#FDFBF7] border border-stone-200 rounded-lg p-4 mb-5">
                    <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-2">Class schedule</div>
                    <ul className="text-sm text-stone-700 grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
                      {items.slice(0, 8).map((s, i) => (
                        <li key={s.id} className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-[#1b4a76]/10 text-[#1b4a76] text-xs font-bold inline-flex items-center justify-center">{i + 1}</span>
                          {new Date(s.starts_at).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", timeZone: "Australia/Brisbane" })} • {new Date(s.starts_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", timeZone: "Australia/Brisbane" })}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to={pickServiceSlug(series.name) ? `/services/${pickServiceSlug(series.name)}` : "/services"} className="btn-secondary text-sm inline-flex items-center gap-2 mr-2" data-testid={`details-${series.series_id || series.id}`}>
                    More info
                  </Link>
                  <button
                    onClick={() => { if (!full) { setEnrolFor(first); setEnrolSent(false); setStep(1); setPrereqChecks({}); setEnrolForm(EMPTY_FORM); setAgeError(""); } }}
                    disabled={full}
                    className={`btn-primary inline-flex items-center gap-2 text-sm ${full ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid={`enrol-${series.series_id || series.id}`}
                  >
                    {full
                      ? "Class full"
                      : (first.payment_mode === "request_only" ? "Request a spot" : "Pay & enrol")}
                    {!full && <ArrowRight size={16} weight="bold" />}
                  </button>
                </article>
              );
            })
          )}
        </div>

        <div className="mt-16 card-pp p-10 lg:p-14 bg-stone-100 text-center">
          <Editable id="courses_inperson.cta.heading" as="h2" className="font-heading text-2xl lg:text-3xl font-bold text-stone-900 mb-3" defaultHtml="Can&#39;t make a class?" />
          <Editable id="courses_inperson.cta.body" as="p" className="text-stone-600 mb-6 max-w-2xl mx-auto" defaultHtml="Our online courses give you the same training framework, on your schedule." />
          <Link to="/courses/online" className="btn-secondary inline-flex items-center gap-2">
            <Editable id="courses_inperson.cta.button" inline as="span" defaultHtml="See Online Courses" /> <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      <PublicFooter />
      <ChatWidget />
      <ExitIntentPopup />

      {enrolFor && (
        <div className="fixed inset-0 bg-stone-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={closeEnrol}>
          <div className="bg-white rounded-2xl max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="enrol-modal">
            <div className="p-6 border-b border-stone-200 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-[#f38838]">Enrol — Step {step} of 3</div>
                <h2 className="font-heading font-bold text-xl">{enrolFor.title?.replace(/ — Session \d+$/, "")}</h2>
                <div className="text-sm text-stone-500">
                  {new Date(enrolFor.starts_at).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Australia/Brisbane" })} • {enrolFor.location} • ${enrolFor.price}
                </div>
              </div>
              <button onClick={closeEnrol} className="text-stone-500 hover:text-stone-900"><X size={20} /></button>
            </div>

            {/* Step progress bar */}
            <div className="px-6 pt-4">
              <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#f38838] transition-all" style={{ width: `${(step / 3) * 100}%` }} />
              </div>
              <div className="flex justify-between text-[10px] uppercase tracking-wider font-bold text-stone-500 mt-2">
                <span className={step >= 1 ? "text-[#f38838]" : ""}>1. Pet</span>
                <span className={step >= 2 ? "text-[#f38838]" : ""}>2. You</span>
                <span className={step >= 3 ? "text-[#f38838]" : ""}>3. Confirm</span>
              </div>
            </div>

            {enrolSent ? (
              <div className="p-8 text-center" data-testid="enrol-success">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <CheckCircle size={28} weight="duotone" className="text-emerald-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-stone-900 mb-2">Request received!</h3>
                <p className="text-stone-600 mb-5">the team will personally review your request and email you within 24 hours to confirm your spot and send the payment link.</p>
                <div className="text-left bg-stone-50 border border-stone-200 rounded-xl p-4 mb-6 text-sm text-stone-700">
                  <div className="font-semibold text-stone-900 mb-2">What happens next</div>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>You'll get a confirmation email at <strong>{enrolForm.email}</strong> — check your junk folder if it doesn't land in 5 minutes.</li>
                    <li>the team reviews your dog's details to make sure the class is the right fit.</li>
                    <li>She'll email a Stripe payment link to lock in your spot.</li>
                  </ol>
                </div>
                <button onClick={closeEnrol} className="btn-primary" data-testid="enrol-success-done">Done</button>
              </div>
            ) : step === 1 ? (
              <div className="p-6 space-y-3" data-testid="enrol-step-1">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-wider font-semibold text-stone-500 mb-1">Dog's name *</label>
                    <input data-testid="enrol-dog-name" required value={enrolForm.dog_name} onChange={(e) => setEnrolForm({ ...enrolForm, dog_name: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider font-semibold text-stone-500 mb-1">Date of birth {(enrolFor.max_age_weeks || enrolFor.min_age_weeks) ? "*" : "(optional)"}</label>
                    <input
                      data-testid="enrol-dog-dob"
                      type="date"
                      required={Boolean(enrolFor.max_age_weeks || enrolFor.min_age_weeks)}
                      value={enrolForm.dog_dob}
                      onChange={(e) => { setEnrolForm({ ...enrolForm, dog_dob: e.target.value }); setAgeError(""); }}
                      className="w-full rounded-lg border border-stone-200 px-3 py-2.5"
                    />
                  </div>
                </div>
                {(enrolFor.max_age_weeks || enrolFor.min_age_weeks) && (
                  <p className="text-[11px] text-stone-500">This class is for puppies {enrolFor.min_age_weeks ? `from ${enrolFor.min_age_weeks}` : ""} {enrolFor.max_age_weeks ? `up to ${enrolFor.max_age_weeks}` : ""} weeks at the first class.</p>
                )}
                {ageError && (
                  <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="age-error">{ageError}</div>
                )}

                {(enrolFor.prerequisites || []).length > 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3 space-y-2">
                    <div className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Please confirm</div>
                    {enrolFor.prerequisites.map((p) => (
                      <label key={p} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`prereq-${p.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                        <input type="checkbox" checked={!!prereqChecks[p]} onChange={(e) => setPrereqChecks({ ...prereqChecks, [p]: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#f38838]" />
                        <span className="text-stone-700">{p}</span>
                      </label>
                    ))}
                  </div>
                )}

                <textarea data-testid="enrol-notes" rows={2} placeholder="Anything else the team should know about your dog?" value={enrolForm.notes} onChange={(e) => setEnrolForm({ ...enrolForm, notes: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5" />

                <button
                  type="button"
                  data-testid="enrol-next-1"
                  disabled={!enrolForm.dog_name || !!ageError}
                  onClick={() => {
                    const err = validateAge();
                    if (err) { setAgeError(err); return; }
                    if (!prereqsOk()) { setAgeError("Please tick every prerequisite above before continuing."); return; }
                    if (!enrolForm.dog_name) { setAgeError("Please enter your dog's name."); return; }
                    setAgeError("");
                    setStep(2);
                  }}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue → Your details
                </button>
              </div>
            ) : step === 2 ? (
              <div className="p-6 space-y-3" data-testid="enrol-step-2">
                {user ? (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-900 flex items-center gap-2" data-testid="enrol-step-2-loggedin">
                    <CheckCircle size={18} weight="fill" className="text-emerald-700" />
                    <div>Signed in as <strong>{user.name}</strong> ({user.email}) — your booking will land in your portal automatically.</div>
                  </div>
                ) : (
                  <p className="text-xs text-stone-500">Your details — we'll use these for your booking confirmation. No password needed yet; you can log in or create an account after payment.</p>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="enrol-name" required placeholder="Your name *" value={enrolForm.name} onChange={(e) => setEnrolForm({ ...enrolForm, name: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5" />
                  <input data-testid="enrol-email" required type="email" placeholder="Email *" value={enrolForm.email} onChange={(e) => setEnrolForm({ ...enrolForm, email: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5" />
                  <input data-testid="enrol-phone" type="tel" placeholder="Phone" value={enrolForm.phone} onChange={(e) => setEnrolForm({ ...enrolForm, phone: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5" />
                  <input data-testid="enrol-suburb" type="text" placeholder="Suburb" value={enrolForm.suburb} onChange={(e) => setEnrolForm({ ...enrolForm, suburb: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5" />
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setStep(1)} className="text-stone-600 px-4">← Back</button>
                  <button
                    type="button"
                    data-testid="enrol-next-2"
                    onClick={() => {
                      if (!enrolForm.name || !enrolForm.email) { setAgeError("Name and email required."); return; }
                      setAgeError("");
                      setStep(3);
                    }}
                    className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
                  >
                    Continue → Confirm
                  </button>
                </div>
                {ageError && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{ageError}</div>}
              </div>
            ) : (
              <form onSubmit={submitEnrol} className="p-6 space-y-3" data-testid="enrol-step-3">
                <div className="rounded-xl bg-stone-50 border border-stone-200 p-4 text-sm">
                  <div className="font-heading font-semibold text-stone-900 mb-1">Review</div>
                  <ul className="text-stone-700 space-y-1">
                    <li><span className="text-stone-500">Class:</span> {enrolFor.title?.replace(/ — Session \d+$/, "")} ({new Date(enrolFor.starts_at).toLocaleDateString("en-AU", { timeZone: "Australia/Brisbane" })})</li>
                    <li><span className="text-stone-500">Dog:</span> {enrolForm.dog_name}{enrolForm.dog_dob ? ` • DOB ${enrolForm.dog_dob}` : ""}</li>
                    <li><span className="text-stone-500">You:</span> {enrolForm.name} • {enrolForm.email}</li>
                    <li>
                      <span className="text-stone-500">Price:</span>{" "}
                      {appliedCoupon ? (
                        <>
                          <span className="line-through text-stone-400">${priceAud.toFixed(2)}</span>{" "}
                          <span className="text-emerald-700 font-semibold">${finalAud.toFixed(2)} AUD</span>
                        </>
                      ) : (
                        <>${priceAud.toFixed(2)} AUD</>
                      )}
                    </li>
                  </ul>
                </div>

                {/* Coupon row — same pattern as ServiceDetail.jsx */}
                {(!enrolFor.payment_mode || enrolFor.payment_mode === "both" || enrolFor.payment_mode === "pay_only") && (
                  <div className="border-t border-stone-100 pt-3" data-testid="enrol-coupon-section">
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm">
                        <span className="text-emerald-800"><strong>{appliedCoupon.code}</strong> applied — saving ${Number(appliedCoupon.discount_aud).toFixed(2)}</span>
                        <button type="button" onClick={removeCoupon} className="text-xs text-emerald-700 hover:underline" data-testid="enrol-coupon-remove">Remove</button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={coupon}
                          onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                          placeholder="Got a discount code? (optional)"
                          className="flex-1 rounded-lg border border-stone-200 px-3 py-2 text-sm font-mono uppercase"
                          data-testid="enrol-coupon-input"
                        />
                        <button
                          type="button"
                          onClick={applyCoupon}
                          disabled={couponBusy || !coupon.trim()}
                          className="px-3 py-2 rounded-lg border border-stone-300 text-sm font-semibold disabled:opacity-50 hover:bg-stone-50"
                          data-testid="enrol-coupon-apply"
                        >
                          {couponBusy ? "..." : "Apply"}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {(!enrolFor.payment_mode || enrolFor.payment_mode === "both" || enrolFor.payment_mode === "request_only") && (
                  <button data-testid="enrol-submit" disabled={enrolBusy} className="w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-stone-50 border-2 border-[#f38838] text-[#f38838] rounded-xl px-6 py-3 font-semibold disabled:opacity-50 transition-colors">
                    {enrolBusy ? "Sending..." : "Request a spot (the team confirms)"}
                  </button>
                )}

                {(!enrolFor.payment_mode || enrolFor.payment_mode === "both" || enrolFor.payment_mode === "pay_only") && (
                  <>
                    {!appliedCoupon && (
                      <p className="text-[11px] text-stone-500 text-center" data-testid="enrol-coupon-hint">
                        Got a discount code? Apply it above before paying.
                      </p>
                    )}
                    <button type="button" onClick={() => payAndEnrol("working")} disabled={paying} className="w-full inline-flex items-center justify-center gap-2 bg-[#1b4a76] hover:bg-[#143a5e] text-white rounded-xl px-6 py-3 font-semibold disabled:opacity-50 transition-colors" data-testid="enrol-pay-now">
                      <CreditCard size={16} weight="fill" /> {paying ? "Redirecting to Stripe…" : `Pay & enrol — $${finalAud.toFixed(0)} AUD`}
                    </button>
                  </>
                )}

                {!user && (enrolFor.payment_mode !== "request_only") && (
                  <p className="text-[11px] text-stone-500 text-center">After payment, you can log in to an existing account or create a new one in one click — no extra forms.</p>
                )}

                <button type="button" onClick={() => setStep(2)} className="text-stone-600 text-sm">← Back</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// "Starting in N days on Sat 8 June" green badge.
// Updates once per minute so a visitor with the page open at midnight sees the
// countdown roll over without a manual reload.
function StartsInBadge({ startsAt, seriesId }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  if (!startsAt) return null;
  const start = new Date(startsAt);
  // "Days until" — compare Brisbane local calendar days so a 9pm Brisbane
  // class on the same day doesn't read as "tomorrow" from a different tz.
  const fmt = (d) => new Intl.DateTimeFormat("en-AU", { timeZone: "Australia/Brisbane", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  const todayKey = fmt(now);
  const startKey = fmt(start);
  // Convert YYYY-MM-DD to a day-number for delta math
  const dayNum = (s) => {
    const [y, m, d] = s.split("/").length === 3 ? s.split("/").reverse() : s.split("-");
    return new Date(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}T00:00:00Z`).getTime() / 86400000;
  };
  const delta = Math.round(dayNum(startKey) - dayNum(todayKey));
  const dateLabel = start.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "long", timeZone: "Australia/Brisbane" });

  let label = "";
  let tone = "emerald"; // emerald = upcoming, amber = imminent, stone = past
  if (delta < 0) {
    label = `Started ${Math.abs(delta)} day${Math.abs(delta) === 1 ? "" : "s"} ago — ${dateLabel}`;
    tone = "stone";
  } else if (delta === 0) {
    label = `Starting today — ${dateLabel}`;
    tone = "amber";
  } else if (delta === 1) {
    label = `Starting tomorrow — ${dateLabel}`;
    tone = "amber";
  } else if (delta <= 7) {
    label = `Starting in ${delta} days — ${dateLabel}`;
    tone = "amber";
  } else {
    label = `Starting in ${delta} days — ${dateLabel}`;
    tone = "emerald";
  }

  const palette = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    stone: "bg-stone-100 text-stone-600 border-stone-200",
  }[tone];

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${palette}`}
      data-testid={`starts-in-${seriesId}`}
      title={dateLabel}
    >
      <CalendarCheck size={12} weight="duotone" />
      {label}
    </span>
  );
}
